import { useEffect, useMemo, useState } from "react";
import { Modal, Checkbox, Select, Input, Form, Alert, Descriptions, message, Tag, Table, Button } from "antd";
import debounce from "lodash.debounce";
import { PostAuditoriaClinicaCorreccion } from "../../Service/Service.auditoriaClinica";
import { ControllerDiagnosticoSearch } from "../../Service/Service.consulta";

const { TextArea } = Input;

// AUDITORIA-CLINICA-FASE-2/3/4 -- unico modal de escritura del modulo.
// Permite seleccionar 1+ de las 7 secciones habilitadas (motivo de consulta,
// signos vitales, enfermedad actual, plan de tratamiento, diagnosticos, nota
// de evolucion, recetas) sobre una atencion YA FINALIZADA, adjuntar
// motivo(reason_code)+
// justificacion obligatorios, y enviar SOLO las secciones marcadas -- nunca
// reenvia secciones no tocadas. Antes de guardar exige un paso de
// confirmacion explicito (sin lenguaje de "eliminar"): la correccion queda
// registrada en Auditoria y NO borra el historial clinico previo.
//
// FASE 3 -- diagnosticos: coleccion con diff estructurado. El backend calcula
// added/removed comparando por `iddiagnostico` (identidad estable del
// catalogo CIE); este modal solo envia la COLECCION FINAL deseada + el
// snapshot `_expected` de lo que se mostro como actual (mismo patron de
// concurrencia ya usado en las demas secciones, extendido a una coleccion).
// El selector de busqueda reutiliza `ControllerDiagnosticoSearch`
// (consulta_diagnosticos), el MISMO servicio que ya usa el flujo clinico
// normal (ver ModalPaciente.js) -- nunca un input de texto libre.
//
// FASE 4 -- recetas: la UNICA seccion versionada. La receta emitida no se
// edita ni se borra: se elige una receta VIGENTE y se la SUSTITUYE (el
// backend crea una nueva receta con `reemplaza_a` = original y marca la
// original como "Reemplazada") o se la ANULA (estado "Anulada", sin
// reemplazo). Una receta ya reemplazada/anulada no es seleccionable -- la
// correccion debe partir de la version vigente de la cadena (el backend
// responde 409 si no).
//
// FASE 3 -- nota_evolucion: `nota_evolucion` es 1-fila-por-cita
// sobreescribible en el flujo real (IngresoConsulta.js siempre opera sobre
// `nota_evolucion[0]`), por lo que se corrige con el MISMO patron de UPDATE
// auditado ya usado para enfermedad_actual/plan_tratamiento -- el valor
// anterior queda preservado en el historial de auditoria (before_json),
// aunque el dato clinico vivo se sobreescriba.

export const REASON_CODE_OPTIONS = [
    { value: "ERROR_DIGITACION", label: "Error de digitación" },
    { value: "INFORMACION_INCOMPLETA", label: "Información incompleta" },
    { value: "ORDEN_INCORRECTA", label: "Orden incorrecta" },
    { value: "DIAGNOSTICO_INCORRECTO", label: "Diagnóstico incorrecto" },
    { value: "CORRECCION_CLINICA", label: "Corrección clínica" },
    { value: "OTRO", label: "Otro" },
];

const SECTION_LABELS = {
    motivo_consulta: "Motivo de consulta",
    signos_vitales: "Signos vitales",
    enfermedad_actual: "Enfermedad actual",
    plan_tratamiento: "Plan de tratamiento",
    diagnosticos: "Diagnósticos",
    nota_evolucion: "Nota de evolución",
    recetas: "Recetas",
};

const fmtDiagnostico = (d) => [d?.codigo, d?.nombre].filter(Boolean).join(" - ") || `CIE #${d?.iddiagnostico}`;

// MODAL-45 §7: signos corregibles desde Auditoría, uno a uno.
const SIGNOS_EDITABLES = ["presion", "pulso", "temperatura", "saturacion_oxigeno"];
const SIGNOS_LABEL = {
    presion: "Presión",
    pulso: "Pulso",
    temperatura: "Temperatura",
    saturacion_oxigeno: "Saturación O2",
};

// FASE 4 -- recetas. `estado` es NOT NULL DEFAULT 'Activa' en BD; se
// normaliza igual para no depender del default al leer filas historicas.
const RECETA_CAMPOS = ["medicamentos", "indicaciones", "posologia", "cantidad", "dosis"];
const RECETA_CAMPOS_LABEL = {
    medicamentos: "Medicamento",
    indicaciones: "Indicaciones",
    posologia: "Posología",
    cantidad: "Cantidad",
    dosis: "Dosis",
};
export const recetaEstado = (r) => r?.estado || "Activa";
export const esRecetaVigente = (r) => recetaEstado(r) === "Activa";
const fmtReceta = (r) => (r ? `${r.medicamentos || "—"} — ${r.indicaciones || "—"}` : "—");

// WORKFLOW-44 §21: controles de motivo+justificacion PROPIOS de un cambio.
// Se repiten por seccion (y por cada ADD/REMOVE de diagnostico) en vez de una
// sola justificacion global, porque dos cambios de la misma correccion pueden
// tener razones clinicas distintas.
export function RazonCambio({ valor, onChange, testId, titulo = "Razón de este cambio" }) {
    return (
        <div className="mt-2" data-testid={testId}>
            <div className="text-muted small mb-1">{titulo}</div>
            <Select
                className="w-100 mb-2"
                placeholder="Motivo del cambio"
                options={REASON_CODE_OPTIONS}
                value={valor?.reason_code}
                onChange={(v) => onChange({ reason_code: v })}
            />
            <TextArea
                rows={2}
                placeholder="Justificación de este cambio (mínimo 10 caracteres)"
                value={valor?.justification || ""}
                onChange={(e) => onChange({ justification: e.target.value })}
            />
        </div>
    );
}

function AuditCorrectionModal({ open, idcita, detalle, onClose, onSaved }) {
    const [checked, setChecked] = useState({
        motivo_consulta: false,
        signos_vitales: false,
        enfermedad_actual: false,
        plan_tratamiento: false,
        diagnosticos: false,
        nota_evolucion: false,
        recetas: false,
    });
    const [motivoValor, setMotivoValor] = useState("");
    const [signosSeleccion, setSignosSeleccion] = useState(null);
    const [signosValores, setSignosValores] = useState({});
    const [enfermedadSeleccion, setEnfermedadSeleccion] = useState(null);
    const [enfermedadValor, setEnfermedadValor] = useState("");
    const [planSeleccion, setPlanSeleccion] = useState(null);
    const [planValor, setPlanValor] = useState("");
    // FASE 3 -- diagnosticos: coleccion editable (agregar/quitar), inicializada
    // desde la coleccion actual observada en pantalla.
    const [diagnosticosSeleccion, setDiagnosticosSeleccion] = useState([]);
    const [diagnosticosOpciones, setDiagnosticosOpciones] = useState([]);
    const [diagnosticosBuscando, setDiagnosticosBuscando] = useState(false);
    const [diagnosticosError, setDiagnosticosError] = useState(null);
    // FASE 3 -- nota_evolucion: UPDATE auditado simple (mismo patron que
    // enfermedad_actual/plan_tratamiento).
    const [evolucionValor, setEvolucionValor] = useState("");
    // FASE 4 -- recetas: la receta NO se edita. Se elige una receta VIGENTE y
    // se la sustituye (se crea una nueva version corregida) o se la anula.
    const [recetaSeleccion, setRecetaSeleccion] = useState(null);
    const [recetaAccion, setRecetaAccion] = useState("REPLACE");
    const [recetaValores, setRecetaValores] = useState({});
    // WORKFLOW-44 §10/§11/§17: motivo+justificacion PROPIOS de cada cambio.
    // `razones` va por SECCION (motivo, signos, enfermedad, plan, evolucion,
    // recetas) y `razonesDiag` por OPERACION de diagnostico ("add:<id>" /
    // "remove:<id>"), porque agregar y retirar un diagnostico son cambios
    // independientes que casi siempre tienen razones distintas.
    const [razones, setRazones] = useState({});
    const [razonesDiag, setRazonesDiag] = useState({});
    const [confirmando, setConfirmando] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);

    const secciones = detalle?.secciones || {};
    const signosVitales = secciones.signos_vitales || [];
    const enfermedadActual = secciones.enfermedad_actual || [];
    const planTratamiento = secciones.plan_tratamiento || [];
    const notaEvolucionActual = (secciones.nota_evolucion || [])[0] || null;
    // Solo las recetas VIGENTES son corregibles: una version ya reemplazada/
    // anulada no admite otra correccion (el backend responde 409), la
    // correccion debe partir siempre de la version vigente de la cadena.
    const recetas = secciones.recetas || [];
    const recetasVigentes = recetas.filter(esRecetaVigente);

    const diagnosticosActualesNormalizados = useMemo(
        () => (secciones.diagnosticos || []).map((d) => ({ iddiagnostico: d.iddiagnostico, codigo: d.diagnostico?.codigo, nombre: d.diagnostico?.nombre })),
        [secciones.diagnosticos]
    );

    // AUDITORIA-CLINICA-WORKFLOW-44 (bug CIE10): antes un fallo de red (o una
    // respuesta que nunca llega) dejaba `diagnosticosOpciones=[]` para
    // siempre, indistinguible de "sin resultados" -- el auditor veia el
    // catalogo "no cargar" sin ningun error. Ahora se distingue explicitamente
    // (mensaje de error en `notFoundContent`) y un timeout defensivo evita que
    // el buscador quede "Buscando..." de forma indefinida si la respuesta
    // nunca se resuelve.
    const buscarDiagnosticos = useMemo(
        () =>
            debounce(async (texto) => {
                if (!texto || texto.trim().length < 3) {
                    setDiagnosticosOpciones([]);
                    setDiagnosticosError(null);
        setRazones({});
        setRazonesDiag({});
                    return;
                }
                setDiagnosticosBuscando(true);
                setDiagnosticosError(null);
                const timeout = new Promise((resolve) =>
                    setTimeout(() => resolve({ success: false, data: [], message: "La búsqueda tardó demasiado. Intenta de nuevo." }), 8000)
                );
                const res = await Promise.race([ControllerDiagnosticoSearch(texto), timeout]);
                setDiagnosticosBuscando(false);
                if (res?.success) {
                    setDiagnosticosOpciones(
                        (res.data || []).map((d) => ({ value: d.id, label: `${d.codigo || ""} - ${d.nombre || ""}`, codigo: d.codigo, nombre: d.nombre }))
                    );
                } else {
                    setDiagnosticosOpciones([]);
                    setDiagnosticosError(res?.message || "No se pudo buscar el diagnóstico.");
                }
            }, 500),
        []
    );

    const resetState = () => {
        setChecked({ motivo_consulta: false, signos_vitales: false, enfermedad_actual: false, plan_tratamiento: false, diagnosticos: false, nota_evolucion: false, recetas: false });
        setMotivoValor(secciones.motivo || "");
        setSignosSeleccion(null);
        setSignosValores({});
        setEnfermedadSeleccion(null);
        setEnfermedadValor("");
        setPlanSeleccion(null);
        setPlanValor("");
        setDiagnosticosSeleccion(diagnosticosActualesNormalizados);
        setDiagnosticosOpciones([]);
        setDiagnosticosError(null);
        setEvolucionValor(notaEvolucionActual?.nota_evolucion || "");
        setRecetaSeleccion(null);
        setRecetaAccion("REPLACE");
        setRecetaValores({});
        setConfirmando(false);
        setErrorMsg(null);
    };

    const handleAfterOpenChange = (isOpen) => {
        if (isOpen) resetState();
    };

    // FASE 3 -- `afterOpenChange` de antd solo dispara cuando termina la
    // transicion CSS del Modal; en algunos entornos (incluidos tests sin
    // animaciones reales) esa transicion nunca "termina", por lo que el
    // reset de las 2 secciones nuevas se refuerza aqui con un efecto atado
    // directamente a `open` -- no depende de ninguna animacion.
    useEffect(() => {
        if (open) {
            setDiagnosticosSeleccion(diagnosticosActualesNormalizados);
            setEvolucionValor(notaEvolucionActual?.nota_evolucion || "");
            setRecetaSeleccion(null);
            setRecetaAccion("REPLACE");
            setRecetaValores({});
            setRazones({});
            setRazonesDiag({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, idcita]);

    const signosRegistro = signosVitales.find((r) => r.idsignosvitales === signosSeleccion) || null;
    const enfermedadRegistro = enfermedadActual.find((r) => r.idenfermedad_actual_cita === enfermedadSeleccion) || null;
    const planRegistro = planTratamiento.find((r) => r.idplan_tratamiento === planSeleccion) || null;

    const recetaRegistro = recetasVigentes.find((r) => r.idreceta === recetaSeleccion) || null;
    // Valores de la receta NUEVA: parten de la original y solo se sobreescribe
    // lo que el usuario edite (la original nunca se modifica).
    const recetaNueva = recetaRegistro
        ? RECETA_CAMPOS.reduce(
              (acc, f) => ({ ...acc, [f]: recetaValores[f] !== undefined ? recetaValores[f] : recetaRegistro[f] ?? "" }),
              {}
          )
        : null;
    const recetaHayDiff = !!recetaNueva && RECETA_CAMPOS.some((f) => (recetaNueva[f] || "") !== (recetaRegistro?.[f] || ""));

    const diagnosticosIdsActuales = diagnosticosActualesNormalizados.map((d) => d.iddiagnostico);
    const diagnosticosIdsSeleccion = diagnosticosSeleccion.map((d) => d.iddiagnostico);
    const diagnosticosAgregados = diagnosticosSeleccion.filter((d) => !diagnosticosIdsActuales.includes(d.iddiagnostico));
    const diagnosticosRetirados = diagnosticosActualesNormalizados.filter((d) => !diagnosticosIdsSeleccion.includes(d.iddiagnostico));
    const diagnosticosHayDiff = diagnosticosAgregados.length > 0 || diagnosticosRetirados.length > 0;

    const seccionesSeleccionadas = Object.keys(checked).filter((k) => checked[k]);

    // Mismas reglas que valida el backend por cada detalle.
    const razonValida = (r) => !!r?.reason_code && (r?.justification || "").trim().length >= 10 && (r?.justification || "").trim().length <= 1000;
    const razonDe = (seccion) => razones[seccion] || {};
    const setRazon = (seccion, patch) => setRazones((rs) => ({ ...rs, [seccion]: { ...(rs[seccion] || {}), ...patch } }));
    const razonDiagDe = (clave) => razonesDiag[clave] || {};
    const setRazonDiag = (clave, patch) => setRazonesDiag((rs) => ({ ...rs, [clave]: { ...(rs[clave] || {}), ...patch } }));

    // MODAL-45 §6: "antes == después" NO es un cambio y no debe enviarse -- se
    // evalúa aquí (antes se dejaba llegar al backend, que respondía 400).
    const signosCampoEditado = (f) =>
        !!signosRegistro && signosValores[f] !== undefined && String(signosValores[f]) !== String(signosRegistro[f] ?? "");
    const signosCamposEditados = signosRegistro ? SIGNOS_EDITABLES.filter(signosCampoEditado) : [];
    const hayCambioReal = {
        motivo_consulta: !!motivoValor.trim() && motivoValor.trim() !== (secciones.motivo || "").trim(),
        signos_vitales: !!signosRegistro && signosCamposEditados.length > 0,
        enfermedad_actual: !!enfermedadRegistro && !!enfermedadValor.trim() && enfermedadValor.trim() !== (enfermedadRegistro.enfermedad_actual || "").trim(),
        plan_tratamiento: !!planRegistro && !!planValor.trim() && planValor.trim() !== (planRegistro.plan_tratamiento || "").trim(),
        diagnosticos: diagnosticosHayDiff,
        nota_evolucion: !!notaEvolucionActual && !!evolucionValor.trim() && evolucionValor.trim() !== (notaEvolucionActual.nota_evolucion || "").trim(),
        recetas: !!recetaRegistro && (recetaAccion === "VOID" || (recetaHayDiff && !!(recetaNueva?.medicamentos || "").trim() && !!(recetaNueva?.indicaciones || "").trim())),
    };

    // MODAL-45 §5: una sección seleccionada es válida cuando tiene un cambio
    // REAL y su propia razón+justificación. NUNCA depende de una razón general.
    const seccionValida = (seccion) => {
        if (!hayCambioReal[seccion]) return false;
        if (seccion === "diagnosticos") {
            // Cada ADD y cada REMOVE exige su propia razón (§13).
            for (const d of diagnosticosAgregados) if (!razonValida(razonDiagDe(`add:${d.iddiagnostico}`))) return false;
            for (const d of diagnosticosRetirados) if (!razonValida(razonDiagDe(`remove:${d.iddiagnostico}`))) return false;
            return true;
        }
        if (seccion === "signos_vitales") {
            // Cada signo editado exige su propia razón (§7).
            return signosCamposEditados.every((f) => razonValida(razonDe(`signos_vitales:${f}`)));
        }
        return razonValida(razonDe(seccion));
    };

    // Habilita Guardar cuando hay al menos 1 cambio real completo y TODAS las
    // secciones marcadas son válidas. Sin ninguna dependencia de la cabecera.
    const puedeContinuar =
        seccionesSeleccionadas.length > 0 && seccionesSeleccionadas.every((s) => seccionValida(s));

    // Motivo+justificacion propios del cambio, tal como los espera el backend
    // dentro de `data` (nunca se reusa el de la cabecera).
    const razonPayload = (seccion) => ({
        reason_code: razonDe(seccion).reason_code,
        justification: (razonDe(seccion).justification || "").trim(),
    });
    const razonDiagPayload = (clave) => ({
        reason_code: razonDiagDe(clave).reason_code,
        justification: (razonDiagDe(clave).justification || "").trim(),
    });

    const buildChanges = () => {
        const changes = [];
        if (checked.motivo_consulta) {
            changes.push({ section: "motivo_consulta", data: { motivo: motivoValor, ...razonPayload("motivo_consulta"), _expected: { motivo: secciones.motivo ?? null } } });
        }
        if (checked.signos_vitales && signosRegistro) {
            // MODAL-45 §7: UN cambio (= un detalle auditable) POR CADA signo
            // editado, con su propia razón. `_expected` se acota al campo que
            // ese cambio toca, para que dos correcciones de campos distintos en
            // el mismo batch no se invaliden entre sí.
            for (const f of signosCamposEditados) {
                changes.push({
                    section: "signos_vitales",
                    data: {
                        idsignosvitales: signosRegistro.idsignosvitales,
                        [f]: signosValores[f],
                        ...razonPayload(`signos_vitales:${f}`),
                        _expected: { [f]: signosRegistro[f] ?? null },
                    },
                });
            }
        }
        if (checked.enfermedad_actual && enfermedadRegistro) {
            changes.push({
                section: "enfermedad_actual",
                data: {
                    idenfermedad_actual_cita: enfermedadRegistro.idenfermedad_actual_cita,
                    enfermedad_actual: enfermedadValor,
                    ...razonPayload("enfermedad_actual"),
                    _expected: { enfermedad_actual: enfermedadRegistro.enfermedad_actual ?? null },
                },
            });
        }
        if (checked.plan_tratamiento && planRegistro) {
            changes.push({
                section: "plan_tratamiento",
                data: {
                    idplan_tratamiento: planRegistro.idplan_tratamiento,
                    plan_tratamiento: planValor,
                    ...razonPayload("plan_tratamiento"),
                    _expected: { plan_tratamiento: planRegistro.plan_tratamiento ?? null },
                },
            });
        }
        if (checked.diagnosticos && diagnosticosHayDiff) {
            // WORKFLOW-44 §17: se envian dos listas EXPLICITAS, cada entrada
            // con su propio motivo+justificacion (agregar y retirar son
            // cambios independientes). `_expected` sigue siendo el snapshot de
            // los iddiagnostico observados en pantalla (concurrencia).
            changes.push({
                section: "diagnosticos",
                data: {
                    added: diagnosticosAgregados.map((d) => ({
                        iddiagnostico: d.iddiagnostico,
                        ...razonDiagPayload(`add:${d.iddiagnostico}`),
                    })),
                    removed: diagnosticosRetirados.map((d) => ({
                        iddiagnostico: d.iddiagnostico,
                        ...razonDiagPayload(`remove:${d.iddiagnostico}`),
                    })),
                    _expected: diagnosticosIdsActuales,
                },
            });
        }
        if (checked.nota_evolucion && notaEvolucionActual) {
            changes.push({
                section: "nota_evolucion",
                data: {
                    idnota_evolucion: notaEvolucionActual.idnota_evolucion,
                    nota_evolucion: evolucionValor,
                    ...razonPayload("nota_evolucion"),
                    _expected: { nota_evolucion: notaEvolucionActual.nota_evolucion ?? null },
                },
            });
        }
        if (checked.recetas && recetaRegistro) {
            // `_expected` = snapshot de la receta tal como se mostro en pantalla
            // (incluye `estado`, para que el backend rechace con 409 si entre
            // medias alguien ya la reemplazo/anulo).
            const expected = RECETA_CAMPOS.reduce((acc, f) => ({ ...acc, [f]: recetaRegistro[f] ?? null }), {
                estado: recetaEstado(recetaRegistro),
            });
            changes.push({
                section: "recetas",
                data: {
                    action: recetaAccion,
                    original_id: recetaRegistro.idreceta,
                    ...razonPayload("recetas"),
                    _expected: expected,
                    ...(recetaAccion === "REPLACE"
                        ? {
                              replacement: RECETA_CAMPOS.reduce(
                                  (acc, f) => ({ ...acc, [f]: recetaNueva[f] || null }),
                                  {}
                              ),
                          }
                        : {}),
                },
            });
        }
        return changes;
    };

    const confirmar = () => {
        setErrorMsg(null);
        setConfirmando(true);
    };

    const guardar = async () => {
        setGuardando(true);
        setErrorMsg(null);
        const changes = buildChanges();
        // MODAL-45: sin motivo/justificacion de cabecera -- la explicacion clinica
        // viaja dentro de cada change.data.
        const res = await PostAuditoriaClinicaCorreccion(idcita, { changes });
        setGuardando(false);
        if (res?.success) {
            message.success("Corrección registrada correctamente");
            onSaved && onSaved();
            onClose && onClose();
        } else {
            setErrorMsg(res?.message || "No se pudo registrar la corrección.");
            setConfirmando(false);
        }
    };

    const resumenTexto = useMemo(
        () => seccionesSeleccionadas.map((s) => SECTION_LABELS[s]).join(", "),
        [seccionesSeleccionadas]
    );

    return (
        <Modal
            title={`Corregir atención #${idcita}`}
            open={open}
            onCancel={onClose}
            afterOpenChange={handleAfterOpenChange}
            destroyOnClose
            width={720}
            okText={confirmando ? "Confirmar y guardar" : "Continuar"}
            cancelText="Cancelar"
            onOk={confirmando ? guardar : confirmar}
            okButtonProps={{ disabled: confirmando ? false : !puedeContinuar, loading: guardando }}
        >
            {errorMsg && <Alert type="error" showIcon message={errorMsg} className="mb-3" data-testid="audit-correction-error" />}

            {!confirmando && (
                <Form layout="vertical">
                    <Form.Item label="Secciones a corregir">
                        <div className="d-flex flex-column gap-2">
                            <Checkbox
                                checked={checked.motivo_consulta}
                                onChange={(e) => setChecked((c) => ({ ...c, motivo_consulta: e.target.checked }))}
                            >
                                Motivo de consulta
                            </Checkbox>
                            {checked.motivo_consulta && (
                                <div className="ps-4">
                                    <div className="text-muted mb-1">Valor actual: {secciones.motivo || "Sin motivo registrado"}</div>
                                    <TextArea
                                        rows={2}
                                        value={motivoValor}
                                        onChange={(e) => setMotivoValor(e.target.value)}
                                        placeholder="Nuevo motivo de consulta"
                                    />
                                    <RazonCambio testId="razon-motivo_consulta" valor={razonDe("motivo_consulta")} onChange={(patch) => setRazon("motivo_consulta", patch)} />
                                </div>
                            )}

                            <Checkbox
                                checked={checked.signos_vitales}
                                onChange={(e) => setChecked((c) => ({ ...c, signos_vitales: e.target.checked }))}
                                disabled={signosVitales.length === 0}
                            >
                                Signos vitales {signosVitales.length === 0 && <span className="text-muted">{"— sin registro en esta atención para corregir"}</span>}
                            </Checkbox>
                            {checked.signos_vitales && (
                                <div className="ps-4">
                                    <Select
                                        placeholder="Selecciona el registro a corregir"
                                        className="mb-2 w-100"
                                        value={signosSeleccion}
                                        onChange={(v) => { setSignosSeleccion(v); setSignosValores({}); }}
                                        options={signosVitales.map((r) => ({
                                            value: r.idsignosvitales,
                                            label: `${r.fecha || ""} ${r.hora || ""} — Presión ${r.presion || "—"} · Pulso ${r.pulso ?? "—"}`,
                                        }))}
                                    />
                                    {/* MODAL-45 §7: corrección POR CAMPO. Cada signo editado
                                        es un cambio lógico independiente, con su propio
                                        motivo+justificación, y produce SU PROPIO detalle
                                        auditable -- nunca se empaquetan dos signos
                                        corregidos por causas distintas bajo una sola
                                        justificación. */}
                                    {signosRegistro && (
                                        <>
                                            {SIGNOS_EDITABLES.map((f) => {
                                                const antes = signosRegistro[f];
                                                const editado = signosCampoEditado(f);
                                                return (
                                                    <div key={f} className="mb-2" data-testid={`signo-${f}`}>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <span style={{ minWidth: 130 }}>{SIGNOS_LABEL[f]}</span>
                                                            <span className="text-muted">Antes: {antes ?? "—"}</span>
                                                            <Input
                                                                style={{ width: 160 }}
                                                                placeholder="Después"
                                                                value={signosValores[f] !== undefined ? signosValores[f] : antes ?? ""}
                                                                onChange={(e) => setSignosValores((v) => ({ ...v, [f]: e.target.value }))}
                                                            />
                                                        </div>
                                                        {editado && (
                                                            <RazonCambio
                                                                testId={`razon-signo-${f}`}
                                                                valor={razonDe(`signos_vitales:${f}`)}
                                                                onChange={(patch) => setRazon(`signos_vitales:${f}`, patch)}
                                                            />
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </>
                                    )}
                                </div>
                            )}

                            <Checkbox
                                checked={checked.enfermedad_actual}
                                onChange={(e) => setChecked((c) => ({ ...c, enfermedad_actual: e.target.checked }))}
                                disabled={enfermedadActual.length === 0}
                            >
                                Enfermedad actual {enfermedadActual.length === 0 && <span className="text-muted">{"— sin registro en esta atención para corregir"}</span>}
                            </Checkbox>
                            {checked.enfermedad_actual && (
                                <div className="ps-4">
                                    <Select
                                        placeholder="Selecciona el registro a corregir"
                                        className="mb-2 w-100"
                                        value={enfermedadSeleccion}
                                        onChange={(v) => {
                                            setEnfermedadSeleccion(v);
                                            const r = enfermedadActual.find((x) => x.idenfermedad_actual_cita === v);
                                            setEnfermedadValor(r?.enfermedad_actual || "");
                                        }}
                                        options={enfermedadActual.map((r) => ({
                                            value: r.idenfermedad_actual_cita,
                                            label: (r.enfermedad_actual || "").slice(0, 60) || `Registro #${r.idenfermedad_actual_cita}`,
                                        }))}
                                    />
                                    {enfermedadRegistro && (
                                        <>
                                            <div className="text-muted mb-1">Valor actual: {enfermedadRegistro.enfermedad_actual}</div>
                                            <TextArea rows={2} value={enfermedadValor} onChange={(e) => setEnfermedadValor(e.target.value)} />
                                            <RazonCambio testId="razon-enfermedad_actual" valor={razonDe("enfermedad_actual")} onChange={(patch) => setRazon("enfermedad_actual", patch)} />
                                        </>
                                    )}
                                </div>
                            )}

                            <Checkbox
                                checked={checked.plan_tratamiento}
                                onChange={(e) => setChecked((c) => ({ ...c, plan_tratamiento: e.target.checked }))}
                                disabled={planTratamiento.length === 0}
                            >
                                Plan de tratamiento {planTratamiento.length === 0 && <span className="text-muted">{"— sin registro en esta atención para corregir"}</span>}
                            </Checkbox>
                            {checked.plan_tratamiento && (
                                <div className="ps-4">
                                    <Select
                                        placeholder="Selecciona el registro a corregir"
                                        className="mb-2 w-100"
                                        value={planSeleccion}
                                        onChange={(v) => {
                                            setPlanSeleccion(v);
                                            const r = planTratamiento.find((x) => x.idplan_tratamiento === v);
                                            setPlanValor(r?.plan_tratamiento || "");
                                        }}
                                        options={planTratamiento.map((r) => ({
                                            value: r.idplan_tratamiento,
                                            label: (r.plan_tratamiento || "").slice(0, 60) || `Registro #${r.idplan_tratamiento}`,
                                        }))}
                                    />
                                    {planRegistro && (
                                        <>
                                            <div className="text-muted mb-1">Valor actual: {planRegistro.plan_tratamiento}</div>
                                            <TextArea rows={2} value={planValor} onChange={(e) => setPlanValor(e.target.value)} />
                                            <RazonCambio testId="razon-plan_tratamiento" valor={razonDe("plan_tratamiento")} onChange={(patch) => setRazon("plan_tratamiento", patch)} />
                                        </>
                                    )}
                                </div>
                            )}
                            <Checkbox
                                checked={checked.diagnosticos}
                                onChange={(e) => setChecked((c) => ({ ...c, diagnosticos: e.target.checked }))}
                            >
                                Diagnósticos
                            </Checkbox>
                            {checked.diagnosticos && (
                                <div className="ps-4">
                                    {/* MODAL-45 §11/§14: cada diagnóstico ACTUAL se marca
                                        para retirar con un botón explícito, y el cambio
                                        queda PENDIENTE (nada toca la BD hasta guardar);
                                        se puede cancelar antes de confirmar. */}
                                    <div className="text-muted mb-1">Diagnósticos actuales:</div>
                                    <div className="mb-2 d-flex flex-column gap-1" data-testid="diagnosticos-actuales">
                                        {diagnosticosActualesNormalizados.length === 0 && (
                                            <span className="text-muted">Esta atención no tiene diagnósticos registrados.</span>
                                        )}
                                        {diagnosticosActualesNormalizados.map((d) => {
                                            const marcado = !diagnosticosSeleccion.some((x) => x.iddiagnostico === d.iddiagnostico);
                                            return (
                                                <div key={d.iddiagnostico} className="d-flex align-items-center justify-content-between gap-2">
                                                    <span className={marcado ? "text-decoration-line-through text-muted" : ""}>
                                                        {fmtDiagnostico(d)}
                                                        {marcado && <Tag color="red" className="ms-2">Se retirará</Tag>}
                                                    </span>
                                                    <Button
                                                        size="small"
                                                        danger={!marcado}
                                                        data-testid={`diag-${marcado ? "cancelar-retiro" : "retirar"}-${d.iddiagnostico}`}
                                                        onClick={() =>
                                                            setDiagnosticosSeleccion((sel) =>
                                                                marcado
                                                                    ? [...sel, d]
                                                                    : sel.filter((x) => x.iddiagnostico !== d.iddiagnostico)
                                                            )
                                                        }
                                                    >
                                                        {marcado ? "Cancelar retiro" : "Retirar"}
                                                    </Button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <Select
                                        showSearch
                                        allowClear
                                        placeholder="Buscar diagnóstico (CIE) para agregar..."
                                        className="w-100 mb-2"
                                        filterOption={false}
                                        loading={diagnosticosBuscando}
                                        onSearch={buscarDiagnosticos}
                                        notFoundContent={diagnosticosBuscando ? "Buscando..." : diagnosticosError ? <span className="text-danger">{diagnosticosError}</span> : "Sin resultados"}
                                        value={null}
                                        options={diagnosticosOpciones}
                                        onChange={(_value, option) => {
                                            if (!option) return;
                                            setDiagnosticosSeleccion((sel) =>
                                                sel.some((x) => x.iddiagnostico === option.value)
                                                    ? sel
                                                    : [...sel, { iddiagnostico: option.value, codigo: option.codigo, nombre: option.nombre }]
                                            );
                                        }}
                                    />
                                    {/* WORKFLOW-44 §17: agregar y retirar son cambios
                                        independientes -- cada uno pide SU motivo y SU
                                        justificación, nunca se reutiliza la del otro. */}
                                    {diagnosticosAgregados.map((d) => (
                                        <div key={`add-${d.iddiagnostico}`} className="border-start ps-2 mb-2">
                                            <div className="small d-flex align-items-center justify-content-between gap-2">
                                                <span><strong>Agregar:</strong> {fmtDiagnostico(d)}</span>
                                                <Button
                                                    size="small"
                                                    data-testid={`diag-cancelar-add-${d.iddiagnostico}`}
                                                    onClick={() =>
                                                        setDiagnosticosSeleccion((sel) => sel.filter((x) => x.iddiagnostico !== d.iddiagnostico))
                                                    }
                                                >
                                                    Cancelar
                                                </Button>
                                            </div>
                                            <RazonCambio
                                                testId={`razon-diag-add-${d.iddiagnostico}`}
                                                valor={razonDiagDe(`add:${d.iddiagnostico}`)}
                                                onChange={(patch) => setRazonDiag(`add:${d.iddiagnostico}`, patch)}
                                            />
                                        </div>
                                    ))}
                                    {diagnosticosRetirados.map((d) => (
                                        <div key={`remove-${d.iddiagnostico}`} className="border-start ps-2 mb-2">
                                            <div className="small"><strong>Retirar:</strong> {fmtDiagnostico(d)}</div>
                                            <RazonCambio
                                                testId={`razon-diag-remove-${d.iddiagnostico}`}
                                                valor={razonDiagDe(`remove:${d.iddiagnostico}`)}
                                                onChange={(patch) => setRazonDiag(`remove:${d.iddiagnostico}`, patch)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}

                            <Checkbox
                                checked={checked.nota_evolucion}
                                onChange={(e) => setChecked((c) => ({ ...c, nota_evolucion: e.target.checked }))}
                                disabled={!notaEvolucionActual}
                            >
                                Nota de evolución {!notaEvolucionActual && <span className="text-muted">{"— sin registro en esta atención para corregir"}</span>}
                            </Checkbox>
                            {checked.nota_evolucion && notaEvolucionActual && (
                                <div className="ps-4">
                                    <div className="text-muted mb-1">Nota original (queda preservada en el historial de auditoría): {notaEvolucionActual.nota_evolucion || "—"}</div>
                                    <TextArea
                                        rows={2}
                                        value={evolucionValor}
                                        onChange={(e) => setEvolucionValor(e.target.value)}
                                        placeholder="Agregar nota correctiva de evolución"
                                    />
                                    <RazonCambio testId="razon-nota_evolucion" valor={razonDe("nota_evolucion")} onChange={(patch) => setRazon("nota_evolucion", patch)} />
                                </div>
                            )}

                            <Checkbox
                                checked={checked.recetas}
                                onChange={(e) => setChecked((c) => ({ ...c, recetas: e.target.checked }))}
                                disabled={recetasVigentes.length === 0}
                                data-testid="audit-recetas-check"
                            >
                                Recetas {recetasVigentes.length === 0 && <span className="text-muted">— sin recetas vigentes para corregir</span>}
                            </Checkbox>
                            {checked.recetas && (
                                <div className="ps-4">
                                    <Alert
                                        type="info"
                                        showIcon
                                        className="mb-2"
                                        message="La receta emitida no se edita ni se elimina: se conserva en el historial y se crea una nueva versión corregida."
                                    />
                                    <Select
                                        placeholder="Selecciona la receta vigente a corregir"
                                        className="mb-2 w-100"
                                        value={recetaSeleccion}
                                        onChange={(v) => { setRecetaSeleccion(v); setRecetaValores({}); }}
                                        options={recetasVigentes.map((r) => ({
                                            value: r.idreceta,
                                            label: `#${r.idreceta} — ${fmtReceta(r)}`,
                                        }))}
                                    />
                                    {recetaRegistro && (
                                        <>
                                            <Select
                                                className="mb-2 w-100"
                                                value={recetaAccion}
                                                onChange={(v) => setRecetaAccion(v)}
                                                options={[
                                                    { value: "REPLACE", label: "Sustituir receta (crea una nueva versión corregida)" },
                                                    { value: "VOID", label: "Anular receta (sin reemplazo)" },
                                                ]}
                                            />
                                            <Descriptions size="small" column={1} bordered className="mb-2" title="Receta actual">
                                                {RECETA_CAMPOS.map((f) => (
                                                    <Descriptions.Item key={f} label={RECETA_CAMPOS_LABEL[f]}>
                                                        {recetaRegistro[f] || "—"}
                                                    </Descriptions.Item>
                                                ))}
                                            </Descriptions>
                                            {recetaAccion === "REPLACE" ? (
                                                <>
                                                    <div className="text-muted mb-1">Nueva receta corregida:</div>
                                                    {RECETA_CAMPOS.map((f) => (
                                                        <Input
                                                            key={f}
                                                            className="mb-2"
                                                            placeholder={RECETA_CAMPOS_LABEL[f]}
                                                            value={recetaNueva[f] || ""}
                                                            onChange={(e) => setRecetaValores((v) => ({ ...v, [f]: e.target.value }))}
                                                        />
                                                    ))}
                                                </>
                                            ) : (
                                                <Alert
                                                    type="warning"
                                                    showIcon
                                                    message="La receta quedará anulada sin reemplazo. Se conserva en el historial como registro histórico."
                                                />
                                            )}
                                            <RazonCambio testId="razon-recetas" valor={razonDe("recetas")} onChange={(patch) => setRazon("recetas", patch)} />
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </Form.Item>

                    {/* MODAL-45 §3/§4: NO existe una justificación general. Cada
                        cambio trae la suya, arriba, junto al propio cambio -- pedir
                        además una explicación global obligaba al auditor a repetirse
                        y era lo que mantenía "Guardar" deshabilitado. */}
                </Form>
            )}

            {confirmando && (
                <div data-testid="audit-correction-confirm">
                    <Alert
                        type="warning"
                        showIcon
                        message="Confirma la corrección"
                        description="Esta acción queda registrada en Auditoría Clínica y NO borra el historial clínico previo: el valor anterior sigue disponible en el historial de correcciones de esta atención."
                        className="mb-3"
                    />
                    <p>Se corregirán las siguientes secciones:</p>
                    <ul>
                        {seccionesSeleccionadas.map((s) => (
                            <li key={s}>{SECTION_LABELS[s]}</li>
                        ))}
                    </ul>
                    {checked.diagnosticos && (
                        <div className="mb-2 small">
                            <div><strong>Diagnósticos a agregar:</strong> {diagnosticosAgregados.length > 0 ? diagnosticosAgregados.map(fmtDiagnostico).join(", ") : "Ninguno"}</div>
                            <div><strong>Diagnósticos a retirar:</strong> {diagnosticosRetirados.length > 0 ? diagnosticosRetirados.map(fmtDiagnostico).join(", ") : "Ninguno"}</div>
                        </div>
                    )}
                    {checked.recetas && recetaRegistro && (
                        <div className="mb-2" data-testid="audit-recetas-confirm">
                            <Alert
                                type="info"
                                showIcon
                                className="mb-2"
                                message={
                                    recetaAccion === "REPLACE"
                                        ? "La receta original quedará preservada en el historial. Se creará una nueva receta corregida y la anterior quedará marcada como reemplazada."
                                        : "La receta original quedará preservada en el historial y quedará marcada como anulada, sin reemplazo."
                                }
                            />
                            <Table
                                size="small"
                                pagination={false}
                                rowKey="campo"
                                dataSource={RECETA_CAMPOS.map((f) => ({
                                    campo: RECETA_CAMPOS_LABEL[f],
                                    antes: recetaRegistro[f] || "—",
                                    despues: recetaAccion === "REPLACE" ? recetaNueva[f] || "—" : "Anulada",
                                }))}
                                columns={[
                                    { title: "Campo", dataIndex: "campo", key: "campo" },
                                    { title: "ANTES", dataIndex: "antes", key: "antes" },
                                    { title: "DESPUÉS", dataIndex: "despues", key: "despues" },
                                ]}
                            />
                        </div>
                    )}
                    {/* Resumen de la razón de CADA cambio (ya no hay una general). */}
                    <Descriptions size="small" column={1} bordered className="mt-2" data-testid="audit-confirm-razones">
                        {seccionesSeleccionadas
                            .filter((s) => s !== "diagnosticos")
                            .map((s) => (
                                <Descriptions.Item key={s} label={SECTION_LABELS[s]}>
                                    {REASON_CODE_OPTIONS.find((r) => r.value === razonDe(s).reason_code)?.label || "—"}
                                    {": "}
                                    {razonDe(s).justification || "—"}
                                </Descriptions.Item>
                            ))}
                        {checked.diagnosticos &&
                            diagnosticosAgregados.map((d) => (
                                <Descriptions.Item key={`c-add-${d.iddiagnostico}`} label={`Agregar ${fmtDiagnostico(d)}`}>
                                    {REASON_CODE_OPTIONS.find((r) => r.value === razonDiagDe(`add:${d.iddiagnostico}`).reason_code)?.label || "—"}
                                    {": "}
                                    {razonDiagDe(`add:${d.iddiagnostico}`).justification || "—"}
                                </Descriptions.Item>
                            ))}
                        {checked.diagnosticos &&
                            diagnosticosRetirados.map((d) => (
                                <Descriptions.Item key={`c-rem-${d.iddiagnostico}`} label={`Retirar ${fmtDiagnostico(d)}`}>
                                    {REASON_CODE_OPTIONS.find((r) => r.value === razonDiagDe(`remove:${d.iddiagnostico}`).reason_code)?.label || "—"}
                                    {": "}
                                    {razonDiagDe(`remove:${d.iddiagnostico}`).justification || "—"}
                                </Descriptions.Item>
                            ))}
                    </Descriptions>
                    <div className="d-none">{resumenTexto}</div>
                </div>
            )}
        </Modal>
    );
}

export default AuditCorrectionModal;
